import React from 'react';
import { Box, Typography, ButtonBase, Alert, Stack } from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import {
  dayLabel,
  mesoDateRange,
  type MesoCalendar,
  type CalendarDay,
  type DayStatus,
  type OfficialTotals,
  type PlannedWorkout,
  type CompletedWorkout,
} from '../lib/activityCalendar';
import { toOneDecimal } from '../lib/activitySummary';

interface ActivityCalendarProps {
  calendar: MesoCalendar;
  official: OfficialTotals | null;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Color is never the only signal: each status also carries a symbol.
const STATUS: Record<DayStatus, { label: string; symbol: string; bg: string; border: string; fg: string }> = {
  done:    { label: 'Done',    symbol: '✓', bg: '#E8F5E9', border: '#66BB6A', fg: '#2E7D32' },
  partial: { label: 'Partly done', symbol: '◐', bg: '#FFF8E1', border: '#FFB300', fg: '#A66A00' },
  missed:  { label: 'Missed',  symbol: '✕', bg: '#FFEBEE', border: '#EF5350', fg: '#C62828' },
  extra:   { label: 'Extra',   symbol: '+', bg: '#EDE7F6', border: '#7E57C2', fg: '#5E35B1' },
  rest:    { label: 'Rest / nothing planned', symbol: '', bg: '#FAFAFA', border: 'rgba(0, 0, 0, 0.08)', fg: '#9E9E9E' },
};

const ACTIVITY_LABELS: Record<string, string> = {
  RUN: 'Run',
  'LONG-RUN': 'Long run',
  WALK: 'Walk',
  'STRENGTH-TRAINING': 'Strength',
  'CROSS-TRAINING': 'Cross-training',
  'REST-DAY': 'Rest day',
};

const activityLabel = (activity: string) =>
  ACTIVITY_LABELS[activity] ??
  activity.toLowerCase().replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase());

const formatDate = (date: string, options: Intl.DateTimeFormatOptions) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', { ...options, timeZone: 'UTC' });

const miles = (value: number) => `${value} mi`;

function describeDay(day: CalendarDay): string {
  const parts = [formatDate(day.date, { weekday: 'long', month: 'short', day: 'numeric' })];
  if (day.plannedMiles > 0 || day.plannedStrength > 0) {
    parts.push(`planned ${[
      day.plannedMiles > 0 && miles(day.plannedMiles),
      day.plannedStrength > 0 && `${day.plannedStrength} strength`,
    ].filter(Boolean).join(' and ')}`);
  }
  if (day.completedMiles > 0 || day.completedStrength > 0) {
    parts.push(`completed ${[
      day.completedMiles > 0 && miles(day.completedMiles),
      day.completedStrength > 0 && `${day.completedStrength} strength`,
    ].filter(Boolean).join(' and ')}`);
  }
  parts.push(STATUS[day.status].label);
  return parts.join(', ');
}

function DayCell({ day, label, selected, onSelect }: { day: CalendarDay; label: string; selected: boolean; onSelect: () => void }) {
  const style = STATUS[day.status];
  const showMiles = day.plannedMiles > 0 || day.completedMiles > 0;
  const showStrength = day.plannedStrength > 0 || day.completedStrength > 0;

  return (
    <ButtonBase
      onClick={onSelect}
      aria-label={describeDay(day)}
      aria-pressed={selected}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        minWidth: 0,
        minHeight: { xs: 56, sm: 68 },
        p: { xs: 0.5, sm: 0.75 },
        borderRadius: 1.5,
        bgcolor: style.bg,
        border: '1px solid',
        borderColor: selected ? '#764ba2' : style.border,
        boxShadow: selected ? '0 0 0 2px rgba(118, 75, 162, 0.35)' : 'none',
        textAlign: 'left',
        transition: 'box-shadow 0.2s ease',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '2px' }}>
        <Typography sx={{ fontSize: { xs: '0.55rem', sm: '0.7rem' }, color: '#888', lineHeight: 1, whiteSpace: 'nowrap', letterSpacing: { xs: '-0.02em', sm: 'normal' } }}>
          {label}
        </Typography>
        {/* A phone-width cell can't fit "Aug 24" and the symbol side by side;
            the status still shows by color, in the aria-label and in the detail. */}
        {style.symbol && (
          <Typography aria-hidden sx={{
            display: { xs: /\D/.test(label) ? 'none' : 'block', sm: 'block' },
            fontSize: { xs: '0.6rem', sm: '0.75rem' }, color: style.fg, fontWeight: 700, lineHeight: 1, flexShrink: 0,
          }}>
            {style.symbol}
          </Typography>
        )}
      </Box>

      {showMiles && (
        <Box sx={{ mt: 0.5, lineHeight: 1 }}>
          <Typography component="span" sx={{ fontSize: { xs: '0.72rem', sm: '0.85rem' }, fontWeight: 700, color: '#333' }}>
            {day.completedMiles}
          </Typography>
          {day.plannedMiles > 0 && (
            <Typography component="span" sx={{ fontSize: { xs: '0.58rem', sm: '0.68rem' }, color: '#888' }}>
              /{day.plannedMiles}
            </Typography>
          )}
        </Box>
      )}

      {showStrength && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 'auto', pt: 0.25 }}>
          <FitnessCenterIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: day.completedStrength > 0 ? '#1E88E5' : '#BDBDBD' }} />
          <Typography sx={{ fontSize: { xs: '0.58rem', sm: '0.68rem' }, color: '#666', lineHeight: 1 }}>
            {day.completedStrength}{day.plannedStrength > 0 ? `/${day.plannedStrength}` : ''}
          </Typography>
        </Box>
      )}
    </ButtonBase>
  );
}

function plannedDetail(workout: PlannedWorkout): string {
  return workout.miles ? miles(toOneDecimal(workout.miles)) : '';
}

function completedDetail(workout: CompletedWorkout): string {
  if (workout.strength) return '';
  if (workout.miles === null) return 'not counted';
  if (workout.unit === 'km') return `${toOneDecimal(workout.distance ?? 0)} km → ${miles(toOneDecimal(workout.miles))}`;
  if (workout.unit === 'mi') return miles(toOneDecimal(workout.miles));
  return workout.distance ? 'not counted (no unit)' : '';
}

function WorkoutLine({ workout, completed }: { workout: PlannedWorkout | CompletedWorkout; completed: boolean }) {
  const detail = completed ? completedDetail(workout as CompletedWorkout) : plannedDetail(workout);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.25 }}>
      <Typography sx={{ fontSize: '0.8rem', color: '#444', minWidth: 0, overflowWrap: 'anywhere' }}>
        <Box component="span" sx={{ fontWeight: 600 }}>{activityLabel(workout.activity)}</Box>
        {workout.name && workout.name !== activityLabel(workout.activity) ? ` · ${workout.name}` : ''}
      </Typography>
      {detail && (
        <Typography sx={{ fontSize: '0.8rem', color: '#666', whiteSpace: 'nowrap' }}>{detail}</Typography>
      )}
    </Box>
  );
}

function DayDetail({ day }: { day: CalendarDay }) {
  return (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(102, 126, 234, 0.06)', border: '1px solid rgba(102, 126, 234, 0.2)' }}>
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#333', mb: 1 }}>
        {formatDate(day.date, { weekday: 'long', month: 'short', day: 'numeric' })}
        <Box component="span" sx={{ fontWeight: 500, color: STATUS[day.status].fg, ml: 1 }}>
          {day.status !== 'rest' && STATUS[day.status].label}
        </Box>
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: '#888', mb: 0.25 }}>
            Planned
          </Typography>
          {day.planned.length === 0
            ? <Typography sx={{ fontSize: '0.8rem', color: '#999' }}>Nothing planned</Typography>
            : day.planned.map((w, i) => <WorkoutLine key={i} workout={w} completed={false} />)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: '#888', mb: 0.25 }}>
            Completed
          </Typography>
          {day.completed.length === 0
            ? <Typography sx={{ fontSize: '0.8rem', color: '#999' }}>Nothing logged</Typography>
            : day.completed.map((w, i) => <WorkoutLine key={i} workout={w} completed />)}
        </Box>
      </Stack>
    </Box>
  );
}

function Total({ label, completed, planned, unit, percent }: {
  label: string; completed: number; planned: number; unit: string; percent: number | null;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, p: 1.25, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.03)' }}>
      <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: '#888' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, fontWeight: 700, color: '#333' }}>
        {completed} of {planned}{unit}
        {percent !== null && (
          <Box component="span" sx={{ fontWeight: 600, color: '#764ba2', ml: 1 }}>{percent}%</Box>
        )}
      </Typography>
    </Box>
  );
}

const ActivityCalendar: React.FC<ActivityCalendarProps> = ({ calendar, official }) => {
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);
  const { totals } = calendar;
  const days = calendar.weeks.flatMap(w => w.days);
  const selectedDay = days.find(d => d.date === selectedDate) ?? null;
  const nothingLogged = days.every(d => d.completed.length === 0);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
        <Total label="Mileage" completed={totals.completedMiles} planned={totals.plannedMiles} unit=" mi" percent={totals.mileagePercent} />
        <Total label="Strength sessions" completed={totals.completedStrength} planned={totals.plannedStrength} unit="" percent={totals.strengthPercent} />
      </Stack>

      <Typography sx={{ fontSize: '0.75rem', color: '#666', mb: 1.5 }}>
        Percentages are total completed over total planned for the meso. Runs, long runs and walks
        count toward mileage; strength and cross-training count as strength sessions. Tap a day for details.
      </Typography>

      {calendar.differsFromOfficial && official && (
        <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.8rem', '& .MuiAlert-message': { py: 0.25 } }}>
          Your published score counted {official.completedMiles} of {official.plannedMiles} mi and{' '}
          {official.completedStrength} of {official.plannedStrength} strength sessions
          {official.updatedAt ? ` (last updated ${new Date(official.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` : ''}.
          Your FinalSurge workouts have changed since then, so the days below add up differently.
        </Alert>
      )}

      {nothingLogged && (
        <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.8rem', '& .MuiAlert-message': { py: 0.25 } }}>
          No workouts logged in FinalSurge for this meso.
        </Alert>
      )}

      <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 700, color: '#333', mb: 0.75 }}>
        {mesoDateRange(days[0].date, days[days.length - 1].date)}
      </Typography>

      <Box role="grid" aria-label="Planned and completed workouts by day">
        <Box role="row" sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.5, sm: 0.75 }, mb: 0.5 }}>
          {WEEKDAYS.map(d => (
            <Typography key={d} role="columnheader" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, color: '#888', textAlign: 'center' }}>
              {d}
            </Typography>
          ))}
        </Box>
        {calendar.weeks.map((week, weekIndex) => (
          <Box
            key={week.days[0].date}
            role="row"
            sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.5, sm: 0.75 }, mb: { xs: 0.5, sm: 0.75 } }}
          >
            {week.days.map((day, dayIndex) => (
              <Box key={day.date} role="gridcell" sx={{ display: 'flex', minWidth: 0, '& > *': { flex: 1 } }}>
                <DayCell
                  day={day}
                  label={dayLabel(day.date, weekIndex * 7 + dayIndex)}
                  selected={day.date === selectedDate}
                  onSelect={() => setSelectedDate(day.date === selectedDate ? null : day.date)}
                />
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5, mt: 1 }}>
        {(Object.keys(STATUS) as DayStatus[]).map(status => (
          <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{
              width: 14, height: 14, borderRadius: 0.5, bgcolor: STATUS[status].bg,
              border: `1px solid ${STATUS[status].border}`, color: STATUS[status].fg,
              fontSize: '0.6rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {STATUS[status].symbol}
            </Box>
            <Typography sx={{ fontSize: '0.7rem', color: '#666' }}>{STATUS[status].label}</Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize: '0.7rem', color: '#666' }}>Miles shown as completed/planned</Typography>
      </Box>

      {selectedDay && <DayDetail day={selectedDay} />}
    </Box>
  );
};

export default ActivityCalendar;
